import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { DollarSign, Clock, Calendar, TrendingUp, AlertCircle, RefreshCw, Loader2, FileText, CreditCard } from 'lucide-react';
import { financeService } from '../services/financeService';
import { authService } from '../services/authService';
import SummaryCard from '../components/dashboard/SummaryCard';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';

/**
 * Financial Overview Page
 * Displays financial summary including total revenue, pending payments, and payment schedule
 */
const FinancialOverview = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [overview, setOverview] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [selectedAccountId, setSelectedAccountId] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);

  // Fetch accounts
  useEffect(() => {
    const fetchAccounts = async () => {
      try {
        const response = await authService.getAccounts();
        setAccounts(response.data?.accounts || []);
        if (response.data?.accounts?.length > 0) {
          setSelectedAccountId(response.data.accounts[0].id);
        }
      } catch (err) {
        console.error('Error fetching accounts:', err);
      }
    };
    fetchAccounts();
  }, []);

  // Fetch financial overview when account changes
  useEffect(() => {
    if (selectedAccountId !== null) {
      fetchFinancialOverview();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAccountId]);

  const fetchFinancialOverview = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const params = {};
      if (selectedAccountId) {
        params.account_id = selectedAccountId;
      }
      
      const response = await financeService.getFinancialOverview(params);
      setOverview(response.data);
      setLastUpdated(new Date());
    } catch (err) {
      const errorMessage = err.message || err.toString() || 'Failed to load financial overview';
      setError(errorMessage);
      console.error('Financial overview fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount || 0);
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  // Calculate days until next payment
  const getDaysUntilNextPayment = () => {
    if (!overview?.payment_schedule?.next_expected_payment_date) return null;
    const nextDate = new Date(overview.payment_schedule.next_expected_payment_date);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    nextDate.setHours(0, 0, 0, 0);
    const diffTime = nextDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  if (loading && !overview) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8 overflow-x-hidden">
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4 text-muted-foreground" />
            <p className="text-muted-foreground">Loading financial overview...</p>
          </div>
        </div>
      </div>
    );
  }

  const daysUntilNext = getDaysUntilNextPayment();

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8 overflow-x-hidden">
      {/* Header */}
      <div className="mb-2 sm:mb-3 lg:mb-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
        <div>
          <h1 className="text-lg sm:text-xl lg:text-2xl font-bold mb-0.5">Financial Overview</h1>
          <p className="text-muted-foreground text-xs">View your financial summary and payment schedule</p>
        </div>
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
          {accounts.length > 1 && (
            <Select
              value={selectedAccountId?.toString()}
              onValueChange={(value) => setSelectedAccountId(parseInt(value))}
            >
              <SelectTrigger className="w-[200px]">
                <SelectValue placeholder="Select account" />
              </SelectTrigger>
              <SelectContent>
                {accounts.map(account => (
                  <SelectItem key={account.id} value={account.id.toString()}>
                    {account.account_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={fetchFinancialOverview}
            disabled={loading}
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Error message */}
      {error && (
        <Alert variant="destructive" className="mb-2 sm:mb-3 lg:mb-4">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Summary Cards */}
      {overview && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-2 sm:gap-3 lg:gap-4 mb-2 sm:mb-3 lg:mb-4">
          <SummaryCard
            title="Total Revenue"
            value={overview.total_revenue}
            icon={DollarSign}
            isCurrency={true}
            subtitle="Total revenue from IronSource Ads"
          />
          <SummaryCard
            title="Pending Payments"
            value={overview.pending_payments?.total_amount || 0}
            icon={Clock}
            isCurrency={true}
            subtitle={`${overview.pending_payments?.count || 0} invoice${overview.pending_payments?.count !== 1 ? 's' : ''} pending`}
          />
          <SummaryCard
            title="Next Payment"
            value={overview.payment_schedule?.next_expected_payment_date ? formatDate(overview.payment_schedule.next_expected_payment_date) : 'N/A'}
            icon={Calendar}
            subtitle={daysUntilNext !== null && daysUntilNext >= 0 ? `${daysUntilNext} days remaining` : (overview.payment_schedule?.payment_terms || 'Net 60')}
          />
        </div>
      )}

      {/* Payment Schedule Details */}
      {overview && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-2 sm:gap-3 lg:gap-4 mb-2 sm:mb-3 lg:mb-4">
          {/* Payment Schedule Card */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-sm sm:text-base">
                <Calendar className="h-5 w-5" />
                Payment Schedule
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Payment Terms</label>
                  <div className="mt-1">
                    <Badge variant="outline" className="text-base py-1 px-3">
                      {overview.payment_schedule?.payment_terms || 'Net 60'}
                    </Badge>
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Last Payment Date</label>
                  <p className="text-lg font-semibold mt-1">
                    {overview.payment_schedule?.last_payment_date 
                      ? formatDate(overview.payment_schedule.last_payment_date)
                      : 'No payments yet'}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Next Expected Payment</label>
                  <p className="text-lg font-semibold mt-1">
                    {overview.payment_schedule?.next_expected_payment_date 
                      ? formatDate(overview.payment_schedule.next_expected_payment_date)
                      : 'N/A'}
                  </p>
                  {daysUntilNext !== null && daysUntilNext >= 0 && (
                    <p className="text-sm text-muted-foreground mt-1">
                      {daysUntilNext === 0 
                        ? 'Due today'
                        : daysUntilNext === 1
                        ? 'Due tomorrow'
                        : `${daysUntilNext} days remaining`}
                    </p>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Pending Payments Card */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-sm sm:text-base">
                <Clock className="h-5 w-5" />
                Pending Payments
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Pending Invoices</label>
                  <p className="text-2xl font-bold mt-1">
                    {overview.pending_payments?.count || 0}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Total Amount</label>
                  <p className="text-2xl font-bold mt-1 text-primary">
                    {formatCurrency(overview.pending_payments?.total_amount || 0)}
                  </p>
                </div>
                {overview.pending_payments?.count > 0 && (
                  <div className="pt-4">
                    <Button asChild variant="outline" className="w-full">
                      <Link to="/finance/invoices?status=pending">
                        <FileText className="w-4 h-4 mr-2" />
                        View Pending Invoices
                      </Link>
                    </Button>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Quick Actions */}
      <Card>
        <CardHeader>
          <CardTitle>Quick Actions</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Button asChild variant="outline" className="h-auto py-4">
              <Link to="/finance/invoices">
                <FileText className="w-5 h-5 mr-2" />
                <div className="text-left">
                  <div className="font-semibold">View Invoices</div>
                  <div className="text-xs text-muted-foreground">Manage all invoices</div>
                </div>
              </Link>
            </Button>
            <Button asChild variant="outline" className="h-auto py-4">
              <Link to="/finance/payment-history">
                <CreditCard className="w-5 h-5 mr-2" />
                <div className="text-left">
                  <div className="font-semibold">Payment History</div>
                  <div className="text-xs text-muted-foreground">View payment records</div>
                </div>
              </Link>
            </Button>
            <Button asChild variant="outline" className="h-auto py-4">
              <Link to="/finance/payment-method">
                <CreditCard className="w-5 h-5 mr-2" />
                <div className="text-left">
                  <div className="font-semibold">Payment Method</div>
                  <div className="text-xs text-muted-foreground">Update settings</div>
                </div>
              </Link>
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Last Updated */}
      {lastUpdated && (
        <div className="mt-6 text-center text-sm text-muted-foreground">
          Last updated: {lastUpdated.toLocaleString()}
        </div>
      )}
    </div>
  );
};

export default FinancialOverview;
